import { defineStore } from 'pinia';

import { getPersistingErrorActions } from '@evidentid/dashboard-commons/modules/persisting-error';
import { getSnackbarActions } from '@evidentid/dashboard-commons/modules/snackbar';
import { extensionMimetypes } from '@evidentid/file-utils/mimeTypes';
import { GatewayApiClient } from '@evidentid/gateway-api-client';
import {
    ContractExtractionCriterion,
    ContractExtractedEntityInfo,
} from '@evidentid/tprm-portal-lib/models/contract-extraction';
import { DocumentDataReviewDocument } from '@evidentid/tprm-portal-lib/models/document-data-review';
import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

import { EditableRequirementCriterion } from '@/modules/decisioning-criteria/models/EditableRequirements.model';
import {
    contractExtractionCriterionToEditableRequirementCriterion,
} from '@/modules/decisioning-criteria/utils/requirementsEditorConverters';
import {
    getPendingContractExtractions,
    PendingContractExtraction,
    setPendingExtractionsInStorage,
} from '@/utils/pending-contract-extraction-storage/pendingContractExtractionStorage';

export interface ContractExtractionRequirements {
    gatewayClient: GatewayApiClient;
}

export interface ContractExtractionDocumentsStatus {
    status: OperationStatus;
    documents: DocumentDataReviewDocument[] | null;
}

type ContractExtractionDocumentsById = Record<string, ContractExtractionDocumentsStatus>;

export interface ContractExtractionCriteriaStatus {
    status: OperationStatus;
    originalCriteria: ContractExtractionCriterion[] | null;
    editableCriteria: EditableRequirementCriterion[] | null;
    editableEvaluationRuleRequirementTypes: TprmRequirementType[];
    entityInfo: ContractExtractedEntityInfo | null;
}

type ContractExtractionCriteriaById = Record<string, ContractExtractionCriteriaStatus>;

export interface ContractExtractionState {
    documents: Record<string, ContractExtractionDocumentsById>;
    pendingExtractions: Record<string, PendingContractExtraction[]>;
    isPolling: Record<string, boolean>;
    /** Whole-loop guard: true until all extractions terminal or stopPolling (covers gaps between intervals). */
    hasActivePollingLoop: Record<string, boolean>;
    criteria: Record<string, ContractExtractionCriteriaById>;
}

const pollingTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
const POLLING_INTERVAL_MS = 15000;

function clearScheduledPollingTimeout(rpName: string) {
    const handle = pollingTimeouts.get(rpName);
    if (handle != null) {
        clearTimeout(handle);
        pollingTimeouts.delete(rpName);
    }
}

interface ContractExtractionProgressPollRow {
    contractExtractionId: string;
    response: { actionId?: string | null, error?: boolean } | null;
    unexpectedError: boolean;
}

function categorizeContractExtractionProgressPollRows(rows: ContractExtractionProgressPollRow[]) {
    const terminalIds = new Set<string>();
    const successfulIds = new Set<string>();
    const erroredIds = new Set<string>();
    let hasUnexpectedError = false;

    for (const { contractExtractionId, response, unexpectedError } of rows) {
        if (unexpectedError) {
            hasUnexpectedError = true;
        } else if (response?.actionId != null) {
            terminalIds.add(contractExtractionId);
            successfulIds.add(contractExtractionId);
        } else if (response?.error === true) {
            terminalIds.add(contractExtractionId);
            erroredIds.add(contractExtractionId);
        }
    }

    return { terminalIds, successfulIds, erroredIds, hasUnexpectedError };
}

function createEmptyContractExtractionDocumentsStatus(): ContractExtractionDocumentsStatus {
    return {
        status: OperationStatus.uninitialized,
        documents: null,
    };
}

export function createEmptyContractExtractionCriteriaStatus(): ContractExtractionCriteriaStatus {
    return {
        status: OperationStatus.uninitialized,
        originalCriteria: null,
        editableCriteria: null,
        editableEvaluationRuleRequirementTypes: [],
        entityInfo: null,
    };
}

function getMimeTypeFromFilename(filename: string): string {
    const extension = filename.split('.').pop()?.toLowerCase() || '';
    return extensionMimetypes[`.${extension}`] || 'application/pdf';
}

export const useContractExtractionStore = defineStore('contractExtraction', {
    state: (): ContractExtractionState => ({
        documents: {},
        criteria: {},
        pendingExtractions: {},
        isPolling: {},
        hasActivePollingLoop: {},
    }),
    actions: {
        startLoadingContractExtractionDocuments(payload: { rpName: string, contractExtractionId: string }) {
            this.documents = {
                ...this.documents,
                [payload.rpName]: {
                    ...this.documents[payload.rpName],
                    [payload.contractExtractionId]: {
                        status: OperationStatus.loading,
                        documents: null,
                    },
                },
            };
        },
        finishLoadingContractExtractionDocuments(payload: {
            rpName: string;
            contractExtractionId: string;
            documents: DocumentDataReviewDocument[];
        }) {
            this.documents = {
                ...this.documents,
                [payload.rpName]: {
                    ...this.documents[payload.rpName],
                    [payload.contractExtractionId]: {
                        status: OperationStatus.success,
                        documents: payload.documents,
                    },
                },
            };
        },
        failLoadingContractExtractionDocuments(payload: { rpName: string, contractExtractionId: string }) {
            this.documents = {
                ...this.documents,
                [payload.rpName]: {
                    ...this.documents[payload.rpName],
                    [payload.contractExtractionId]: {
                        status: OperationStatus.error,
                        documents: null,
                    },
                },
            };
        },
        startLoadingContractExtractionCriteria(payload: { rpName: string, contractExtractionId: string }) {
            this.criteria = {
                ...this.criteria,
                [payload.rpName]: {
                    ...this.criteria[payload.rpName],
                    [payload.contractExtractionId]: {
                        ...createEmptyContractExtractionCriteriaStatus(),
                        status: OperationStatus.loading,
                    },
                },
            };
        },
        finishLoadingContractExtractionCriteria(payload: {
            rpName: string;
            contractExtractionId: string;
            originalCriteria: ContractExtractionCriterion[];
            editableCriteria: EditableRequirementCriterion[];
            entityInfo: ContractExtractedEntityInfo;
        }) {
            this.criteria = {
                ...this.criteria,
                [payload.rpName]: {
                    ...this.criteria[payload.rpName],
                    [payload.contractExtractionId]: {
                        status: OperationStatus.success,
                        originalCriteria: payload.originalCriteria,
                        editableCriteria: payload.editableCriteria,
                        editableEvaluationRuleRequirementTypes: [],
                        entityInfo: payload.entityInfo,
                    },
                },
            };
        },
        failLoadingContractExtractionCriteria(payload: { rpName: string, contractExtractionId: string }) {
            this.criteria = {
                ...this.criteria,
                [payload.rpName]: {
                    ...this.criteria[payload.rpName],
                    [payload.contractExtractionId]: {
                        ...createEmptyContractExtractionCriteriaStatus(),
                        status: OperationStatus.error,
                    },
                },
            };
        },
        updateEditableContractExtractionCriteria(payload: {
            rpName: string;
            contractExtractionId: string;
            editableCriteria: EditableRequirementCriterion[];
        }) {
            const currentStatus =
                this.criteria[payload.rpName]?.[payload.contractExtractionId]
                || createEmptyContractExtractionCriteriaStatus();

            this.criteria = {
                ...this.criteria,
                [payload.rpName]: {
                    ...this.criteria[payload.rpName],
                    [payload.contractExtractionId]: {
                        ...currentStatus,
                        editableCriteria: payload.editableCriteria,
                    },
                },
            };
        },
        updateEditableContractExtractionEvaluationRule(payload: {
            rpName: string;
            contractExtractionId: string;
            editableEvaluationRuleRequirementTypes: TprmRequirementType[];
        }) {
            const currentStatus =
                this.criteria[payload.rpName]?.[payload.contractExtractionId]
                || createEmptyContractExtractionCriteriaStatus();

            this.criteria = {
                ...this.criteria,
                [payload.rpName]: {
                    ...this.criteria[payload.rpName],
                    [payload.contractExtractionId]: {
                        ...currentStatus,
                        editableEvaluationRuleRequirementTypes: payload.editableEvaluationRuleRequirementTypes,
                    },
                },
            };
        },
        setPendingExtractions(payload: { rpName: string, extractions: PendingContractExtraction[] }) {
            this.pendingExtractions = {
                ...this.pendingExtractions,
                [payload.rpName]: payload.extractions,
            };
        },
        setPollingStatus(payload: { rpName: string, isPolling: boolean }) {
            this.isPolling = {
                ...this.isPolling,
                [payload.rpName]: payload.isPolling,
            };
        },
        setActivePollingLoop(payload: { rpName: string, active: boolean }) {
            this.hasActivePollingLoop = {
                ...this.hasActivePollingLoop,
                [payload.rpName]: payload.active,
            };
        },
        async loadContractExtractionDocuments(payload: { rpName: string, contractExtractionId: string }) {
            const { rpName, contractExtractionId } = payload;
            const { gatewayClient } = this.$app as ContractExtractionRequirements;
            const currentStatus =
                this.documents[rpName]?.[contractExtractionId] || createEmptyContractExtractionDocumentsStatus();

            if (currentStatus.status === OperationStatus.loading || currentStatus.status === OperationStatus.success) {
                return;
            }

            this.startLoadingContractExtractionDocuments({ rpName, contractExtractionId });

            try {
                const contractDocument = await gatewayClient
                    .submissionTprmUi()
                    .getContractExtractionDocument(rpName, contractExtractionId);
                const latestStatus =
                    this.documents[rpName]?.[contractExtractionId] || createEmptyContractExtractionDocumentsStatus();

                if (latestStatus.status !== OperationStatus.loading &&
                    latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }

                this.finishLoadingContractExtractionDocuments({
                    rpName,
                    contractExtractionId,
                    documents: [ {
                        data: contractDocument.document,
                        mimeType: getMimeTypeFromFilename(contractDocument.filename),
                    } ],
                });
            } catch (error) {
                this.failLoadingContractExtractionDocuments({ rpName, contractExtractionId });
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadContractExtractionCriteria(payload: {
            rpName: string;
            contractExtractionId: string;
        }) {
            const { rpName, contractExtractionId } = payload;
            const { gatewayClient } = this.$app as ContractExtractionRequirements;
            const currentStatus =
                this.criteria[rpName]?.[contractExtractionId] || createEmptyContractExtractionCriteriaStatus();

            if (currentStatus.status === OperationStatus.loading || currentStatus.status === OperationStatus.success) {
                return;
            }

            this.startLoadingContractExtractionCriteria({ rpName, contractExtractionId });

            try {
                const response = await gatewayClient.tprmUi().getContractExtractionData(rpName, contractExtractionId);
                const latestStatus =
                    this.criteria[rpName]?.[contractExtractionId] || createEmptyContractExtractionCriteriaStatus();

                if (latestStatus.status !== OperationStatus.loading &&
                    latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }

                this.finishLoadingContractExtractionCriteria({
                    rpName,
                    contractExtractionId,
                    originalCriteria: response.criteria,
                    editableCriteria: response.criteria.map((criterion) =>
                        contractExtractionCriterionToEditableRequirementCriterion(criterion)),
                    entityInfo: response.entityInfo,
                });
            } catch (error) {
                this.failLoadingContractExtractionCriteria({ rpName, contractExtractionId });
                await getPersistingErrorActions().showError(error);
            }
        },
        async initializePolling(payload: { rpName: string }) {
            const { rpName } = payload;
            const pending = getPendingContractExtractions(rpName);
            this.setPendingExtractions({ rpName, extractions: pending });

            if (this.hasActivePollingLoop[rpName]) {
                return;
            }

            if (pending.length > 0) {
                clearScheduledPollingTimeout(rpName);
                await this.pollOnce({ rpName });
            }
        },
        async pollOnce(payload: { rpName: string }) {
            const { rpName } = payload;
            const { gatewayClient } = this.$app as ContractExtractionRequirements;
            clearScheduledPollingTimeout(rpName);
            this.setPollingStatus({ rpName, isPolling: true });

            try {
                const pendingBefore = this.pendingExtractions[rpName] || [];
                if (!pendingBefore.length) {
                    this.setActivePollingLoop({ rpName, active: false });
                    return;
                }

                this.setActivePollingLoop({ rpName, active: true });

                const responses = await Promise.all(pendingBefore.map(async ({ contractExtractionId }) => {
                    try {
                        const response = await gatewayClient
                            .tprmUi()
                            .getContractExtractionProgress(rpName, contractExtractionId);
                        return { contractExtractionId, response, unexpectedError: false };
                    } catch {
                        return { contractExtractionId, response: null, unexpectedError: true };
                    }
                }));

                const { terminalIds, successfulIds, erroredIds, hasUnexpectedError } =
                    categorizeContractExtractionProgressPollRows(responses);

                const latestPending = getPendingContractExtractions(rpName);
                const stillPending = latestPending.filter(
                    ({ contractExtractionId }) => !terminalIds.has(contractExtractionId),
                );
                setPendingExtractionsInStorage(rpName, stillPending);
                this.setPendingExtractions({ rpName, extractions: stillPending });

                if (!this.hasActivePollingLoop[rpName]) {
                    return;
                }

                if (successfulIds.size > 0) {
                    const successExtractions = pendingBefore.filter(
                        ({ contractExtractionId }) => successfulIds.has(contractExtractionId),
                    );
                    for (const { filename } of successExtractions) {
                        await getSnackbarActions().displaySnackbar({
                            success: true,
                            message: filename
                                ? `${filename} is ready for review. Please visit actions.`
                                : 'Contract Risk Profile is ready for review. Please visit actions.',
                        });
                    }
                }

                if (erroredIds.size > 0) {
                    const errorExtractions = pendingBefore.filter(
                        ({ contractExtractionId }) => erroredIds.has(contractExtractionId),
                    );
                    for (const { filename } of errorExtractions) {
                        await getSnackbarActions().displaySnackbar({
                            success: false,
                            message: filename
                                ? `Failed to process ${filename}. Please try again.`
                                : 'Contract processing failed. Please try again.',
                        });
                    }
                }

                if (hasUnexpectedError) {
                    await getSnackbarActions().displaySnackbar({
                        success: false,
                        message: 'Something unexpected happened while checking contract processing status. Retrying...',
                    });
                }

                if (stillPending.length > 0) {
                    const timeout = setTimeout(() => {
                        this.pollOnce({ rpName });
                    }, POLLING_INTERVAL_MS);
                    pollingTimeouts.set(rpName, timeout);
                } else {
                    this.setActivePollingLoop({ rpName, active: false });
                }
            } catch (error) {
                clearScheduledPollingTimeout(rpName);
                this.setActivePollingLoop({ rpName, active: false });
                throw error;
            } finally {
                this.setPollingStatus({ rpName, isPolling: false });
            }
        },
        stopPolling(payload: { rpName: string }) {
            const { rpName } = payload;
            clearScheduledPollingTimeout(rpName);
            this.setActivePollingLoop({ rpName, active: false });
            this.setPollingStatus({ rpName, isPolling: false });
        },
    },
});
